"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Zap, LayoutGrid, Network, Trophy, ArrowRight, CircleCheck as CheckCircle2, ShieldAlert } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const benefits = [{
  id: "faster-rankings",
  title: "Faster Rankings",
  tagline: "Accelerated Indexation",
  description: "When I clean up your website's core architecture, search engines discover, render, and rank your high-value pages without crawling impediments.",
  keyPoint1: "Core Web Vitals & speed enhancement",
  keyPoint2: "Improved indexation efficiency",
  keyPoint3: "Precise canonical & redirect structures",
  icon: Zap,
  metric: "Improved crawl efficiency",
  metricLabel: "Search bot discovery"
}, {
  id: "user-experience",
  title: "Better User Experience",
  tagline: "Seamless Engagement",
  description: "Technical SEO isn't just for bots—it directly improves how real visitors interact with your site, reducing bounce rates and supporting conversions.",
  keyPoint1: "Mobile-first responsive optimization",
  keyPoint2: "Frictionless navigation & layout stability",
  keyPoint3: "Fast touch responsiveness & interaction",
  icon: LayoutGrid,
  metric: "Better page performance",
  metricLabel: "Core Web Vitals & interaction speed"
}, {
  id: "crawl-efficiency",
  title: "Crawl Efficiency",
  tagline: "Optimized Crawl Budget",
  description: "Search bots have limited time for your website. I eliminate duplicate paths and thin content so search engines prioritize your primary revenue pages.",
  keyPoint1: "Strategic sitemap & robots.txt hierarchy",
  keyPoint2: "Elimination of crawl waste & soft 404s",
  keyPoint3: "Structured data & Schema integration",
  icon: Network,
  metric: "Reduced crawl waste",
  metricLabel: "Prioritised page crawling"
}, {
  id: "competitive-edge",
  title: "Competitive Edge",
  tagline: "Future-Proof Foundation",
  description: "Build a stronger technical foundation to support your wider SEO strategy across traditional and AI search engines.",
  keyPoint1: "AI search & LLM crawler readiness",
  keyPoint2: "Improved technical stability",
  keyPoint3: "Clean site architecture for easy scaling",
  icon: Trophy,
  metric: "Stronger technical foundation",
  metricLabel: "Scalable architecture & AI readiness"
}];
export default function TechnicalSeoBenefits() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const matchMedia = gsap.matchMedia();
    matchMedia.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.from(".benefit-card", {
        y: 40,
        opacity: 0,
        duration: 0.7,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 75%"
        }
      });
      gsap.from(".benefit-header", {
        y: -20,
        opacity: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 85%"
        }
      });
    });
  }, {
    scope: containerRef
  });
  return <section id="technicalseobenefits" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none opacity-40">
        <div className="absolute top-1/4 -left-20 w-80 h-80 rounded-lg bg-secondary blur-3xl" />
        <div className="absolute bottom-1/3 -right-20 w-96 h-96 rounded-lg bg-accent/20 blur-3xl" />
      </div>

      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="benefit-header max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-6">
            Why Technical SEO Matters For Sustainable Growth
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-default">
            {"When I audit and fix your website's technical foundation through "}
            <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              a comprehensive SEO audit
            </Link>
            {", search engines can crawl, index, and rank your content without friction. Resolving silent technical errors and crawl bottlenecks eliminates hidden blockers that suppress organic revenue."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {benefits.map((benefit, i) => {
          const IconComponent = benefit.icon;
          return <div key={benefit.id} data-index={i} className="benefit-card">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl flex flex-col justify-between overflow-hidden group">
                  <CardHeader className="p-6 sm:p-8 pb-4">
                    <div className="flex items-center justify-between gap-4 mb-6">
                      <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="text-xs font-mono font-medium border-border text-muted-foreground">
                        {benefit.tagline}
                      </Badge>
                    </div>

                    <CardTitle className="text-2xl font-semibold text-foreground font-default mb-3 group-hover:text-primary transition-colors">
                      {benefit.title}
                    </CardTitle>

                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed font-default mb-6">
                      {benefit.description}
                    </p>
                  </CardHeader>

                  <CardContent className="p-6 sm:p-8 pt-0 flex-1 flex flex-col justify-end">
                    <div className="space-y-3 pt-4 border-t border-border mb-6">
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>{benefit.keyPoint1}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>{benefit.keyPoint2}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0" />
                        <span>{benefit.keyPoint3}</span>
                      </div>
                    </div>

                    <div className="bg-muted/50 rounded-xl p-4 flex items-center justify-between border border-border">
                      <div>
                        <span className="block text-base sm:text-lg font-semibold font-default text-primary">
                          {benefit.metric}
                        </span>
                        <span className="text-xs text-muted-foreground font-medium">
                          {benefit.metricLabel}
                        </span>
                      </div>
                      <div className="size-8 rounded-lg bg-background flex items-center justify-center text-muted-foreground group-hover:text-primary transition-colors border border-border shrink-0">
                        <ArrowRight className="size-4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-16 lg:mt-20 bg-card border border-border rounded-2xl p-6 sm:p-10 lg:p-12 shadow-sm relative overflow-hidden">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-accent uppercase tracking-wider mb-3">
                <ShieldAlert className="size-4" />
                <span>Hands-on Technical Execution</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-default mb-3">
                Stop losing rankings to hidden technical errors
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                {"I audit complex platforms across "}
                <Link to="/EcommerceSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
                  ecommerce SEO
                </Link>
                {" and "}
                <Link to="/B2BSeoLeadGenerationPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
                  B2B SEO
                </Link>
                {"—identifying javascript rendering issues, slow server responses, and indexation bottlenecks. I recommend practical code fixes, provide developer-ready guidance, and ensure technical changes are properly implemented."}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto shrink-0">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 shadow-sm" asChild>
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-border text-foreground hover:bg-muted font-medium px-6" asChild>
                <Link to="/CaseStudiesPage">
                  View Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
          <span className="font-semibold text-foreground shrink-0">Explore Related SEO Services:</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              Comprehensive SEO audits
            </Link>
            <span className="text-border" aria-hidden="true">•</span>
            <Link to="/EcommerceSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              Ecommerce SEO support
            </Link>
            <span className="text-border" aria-hidden="true">•</span>
            <Link to="/B2BSeoLeadGenerationPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              B2B lead generation strategy
            </Link>
            <span className="text-border" aria-hidden="true">•</span>
            <Link to="/InternationalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              International SEO expertise
            </Link>
            <span className="text-border" aria-hidden="true">•</span>
            <Link to="/LocalSEOPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              Local SEO strategy
            </Link>
            <span className="text-border" aria-hidden="true">•</span>
            <Link to="/AISearchOptimisationPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              AI search optimisation
            </Link>
          </div>
        </div>
      </div>
    </section>;
}